import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  cardIdsFromSourceRef,
  isFlowControlAction,
  type LegalAction,
  type MatchPublicView,
} from '../../api-client.js';
import { CardPreviewModal } from '../CardPreviewModal.js';
import { EventLog } from '../EventLog.js';
import { HandCard, type HandCardData } from '../HandCard.js';
import { MatchClock } from '../MatchClock.js';
import { PositionInspector } from './PositionInspector.js';
import { RecycleCostModal } from './RecycleCostModal.js';
import { humanizeSeatRefs } from '../../lib/seat-labels.js';
import {
  AflClearancePanel,
  AflContestPanel,
  AflRuckPanel,
  AflScoreBoard,
  AflFieldView,
  bestRover,
  humanizePhase,
  Panel,
  roverTierNumber,
  type AflClearanceView,
  type AflRuckView,
  type AflViewerState,
  type OnFieldCardView,
} from './AflView.js';

/**
 * Full-screen, no-page-scroll AFL match view.
 *
 * The whole thing is pinned to one measured viewport box: a slim top bar,
 * the field as the hero (it fills the middle), a compact hand strip along
 * the bottom, and a detail column (score / contest / ruck / clearance /
 * event log) that scrolls INSIDE itself. On phones the detail column folds
 * into a toggled drawer so the field is never pushed off-screen.
 *
 * Disposal is click-to-move: pick one of your glowing player cards, then
 * tap where to send the ball on the field (see DisposalLayer). Buttons only
 * appear for the one genuinely ambiguous case — run vs. forward kick.
 */

/** Disposal verbs that resolve to a field DESTINATION (vs. a plain card play). */
function isFieldTargeting(action: LegalAction): boolean {
  const d = action.descriptorId ?? '';
  return (
    d.startsWith('afl:run:') ||
    d.startsWith('afl:handpass:') ||
    d.startsWith('afl:kick:') ||
    d.startsWith('afl:shot:')
  );
}

/** The recycle cost (cards returned hand→deck) the engine attached, or 0. */
function actionCost(action: LegalAction): number {
  const c = action.sourceRef?.cost;
  return typeof c === 'number' && c > 0 ? c : 0;
}

/** "Tex Walker — Shoot for goal" → "Shoot for goal" (the pinned button already
 *  belongs to the carrier, so the actor prefix is noise). */
function stripActorPrefix(label: string | undefined): string {
  const l = label ?? 'Shoot for goal';
  const i = l.indexOf(' — ');
  return i >= 0 ? l.slice(i + 3) : l;
}

/**
 * Card ids in a sourceRef that are being PLAYED (the committed player and the
 * modifier card) — NOT the cards paid as the cost. Used to take those out of
 * the hand before offering it as the payment pool, so a card can't be both
 * played and recycled in the same act.
 */
const PLAYED_CARD_FIELDS = [
  'cardId', 'handballId', 'dashId', 'tackleId', 'spoilId', 'riderId', 'shotMod', 'torpedo',
] as const;
function playedCardIds(action: LegalAction): string[] {
  const ref = action.sourceRef;
  if (!ref) return [];
  const ids: string[] = [];
  for (const field of PLAYED_CARD_FIELDS) {
    const value = ref[field];
    if (typeof value === 'string' && value.length > 0) ids.push(value);
  }
  // The committed player is played FROM HAND for handpass / tackle / spoil /
  // mark-lead, so exclude it from the payment pool. But for run / shot the
  // playerId is the on-field CARRIER (never a hand card) — keep any hand
  // copies of that same card selectable as payment.
  const move = typeof ref.move === 'string' ? ref.move : '';
  if (move !== 'run' && move !== 'shot') {
    const pid = ref.playerId;
    if (typeof pid === 'string' && pid.length > 0) ids.push(pid);
  }
  return ids;
}

/** Project an on-field card into the hand-card shape the preview modal reads. */
function onFieldToHandCard(item: OnFieldCardView): HandCardData {
  const c = item.card;
  return {
    id: item.cardId,
    kind: c?.kind ?? 'player',
    name: c?.name,
    position: c?.position,
    stats: c?.stats,
  };
}

export type AflMatchProps = {
  view: AflViewerState;
  match: MatchPublicView;
  userSeatId: string | null;
  isParticipant: boolean;
  legalActions: LegalAction[];
  busy: boolean;
  onAct: (action: LegalAction, payIds?: string[]) => void;
  matchId: string;
  seatLabels: Record<string, string>;
  lastEventSummary: string;
  error: string | null;
};

export function AflMatch(props: AflMatchProps) {
  const {
    view,
    match,
    userSeatId,
    isParticipant,
    legalActions,
    busy,
    onAct,
    matchId,
    seatLabels,
    lastEventSummary,
    error,
  } = props;

  const rootRef = useRef<HTMLDivElement>(null);
  const height = useViewportFill(rootRef);
  // The field hugs its 17:10 footprint (height-derived width). We measure that
  // width and cap the hand strip to it, so the hand SCROLLS horizontally inside
  // the arena's width instead of widening the left column past the field (which
  // would re-introduce slate bands beside the arena). CSS can't reference a
  // sibling's height-derived width, so this one measured number breaks the
  // circular dependency.
  const fieldAreaRef = useRef<HTMLDivElement>(null);
  const fieldAreaH = useElementHeight(fieldAreaRef);
  // The field's 17:10 footprint width, derived from its measured height.
  const arenaWidth = fieldAreaH ? Math.round((fieldAreaH * 17) / 10) : undefined;

  const [selectedActorId, setSelectedActorId] = useState<string | null>(null);
  // Mobile (<md): the detail panels live behind a bottom tab strip rather than
  // the desktop side column. 'hand' = play view; the rest overlay a panel slice.
  const [mobileTab, setMobileTab] = useState<'hand' | 'score' | 'live' | 'log'>('hand');
  // Clicking ANY card opens this modal first — it never auto-plays. It carries
  // the card plus whatever it can do right now (commit plays as buttons, and a
  // disposal "aim" affordance), so playable and non-playable cards behave the
  // same: zoom in, read it, then choose. `inspect` is the zoom-only shortcut.
  const [preview, setPreview] = useState<{
    card: HandCardData;
    commitActions: LegalAction[];
    canAim: boolean;
  } | null>(null);
  const inspect = (card: HandCardData) =>
    setPreview({ card, commitActions: [], canAim: false });

  // A costed play awaiting the coach's recycle-payment choice. Set by
  // requestAct when there's a genuine choice; the picker resolves it.
  const [payPrompt, setPayPrompt] = useState<{
    action: LegalAction;
    cost: number;
    cards: HandCardData[];
  } | null>(null);

  // Position inspector: tap a field cell to see which cards each coach has
  // standing there (owner rule #4). Holds the clicked zone; the cards are
  // filtered from view.onField inside the modal.
  const [inspectPos, setInspectPos] = useState<{ name: string; line: string; lane: string } | null>(null);

  // Card id → every legal action referencing it (drives the glow + click).
  const actionsByCardId = useMemo(() => {
    const map = new Map<string, LegalAction[]>();
    for (const action of legalActions) {
      for (const cardId of cardIdsFromSourceRef(action)) {
        const list = map.get(cardId) ?? [];
        list.push(action);
        map.set(cardId, list);
      }
    }
    return map;
  }, [legalActions]);

  // The ball-carrier disposes (run / kick / kick-to-space / shot) DIRECTLY —
  // it needs NO card from hand — so default the selection to the carrier and
  // light up its targets the moment it's your ball. Tapping a hand card
  // switches the selection to that receiver (a handpass); tapping it again
  // (or the carrier) falls back to carrying / kicking it yourself.
  const carrierId = view.carrierCardId ?? null;
  const carrierHasDisposal =
    !!carrierId && (actionsByCardId.get(carrierId) ?? []).some(isFieldTargeting);
  const effectiveActorId = selectedActorId ?? (carrierHasDisposal ? carrierId : null);
  const disposingAsCarrier = !!effectiveActorId && effectiveActorId === carrierId;

  const selectedActions = effectiveActorId
    ? (actionsByCardId.get(effectiveActorId) ?? []).filter(isFieldTargeting)
    : [];

  // Shot options for the effective actor, surfaced as PINNED gold buttons
  // above the hand. The primary action must never live only on the field,
  // where the f50 kick pills bury it on a phone (owner mobile-first rule).
  const shotActions = selectedActions.filter((a) =>
    (a.descriptorId ?? '').startsWith('afl:shot:'),
  );

  // Drop a stale selection when the turn/legal actions change underneath us
  // (opponent moved, our disposal resolved, etc.).
  useEffect(() => {
    if (selectedActorId && !actionsByCardId.has(selectedActorId)) {
      setSelectedActorId(null);
    }
  }, [actionsByCardId, selectedActorId]);

  const userHand: HandCardData[] = userSeatId ? (view.teams[userSeatId]?.hand ?? []) : [];
  const yourTurn = isParticipant && legalActions.length > 0;
  const hasDisposal = legalActions.some(isFieldTargeting);
  const effectiveName =
    userHand.find((c) => c.id === effectiveActorId)?.name ??
    view.onField?.find((c) => c.cardId === effectiveActorId)?.card?.name ??
    'this player';

  // Flow-control verbs that aren't tied to a card or a field destination
  // (kick-in pocket, escalation decline, pass). Concede is excluded — it
  // lives in the top-bar resign control. These previously sat in ActionBar.
  const flowActions = legalActions.filter(
    (a) => isFlowControlAction(a) && a.actionKind !== 'concede',
  );

  const handleCard = (card: HandCardData) => {
    // ALWAYS zoom in first — clicking a card never plays it. The modal shows
    // the card's plays as explicit buttons (commit) plus a disposal "aim"
    // affordance, so playable and non-playable cards behave identically.
    const matching = card.id ? (actionsByCardId.get(card.id) ?? []) : [];
    setPreview({
      card,
      commitActions: matching.filter((a) => !isFieldTargeting(a)),
      canAim: matching.some(isFieldTargeting),
    });
  };

  // Every play funnels through here. A costed play opens the recycle-payment
  // picker FIRST (so the coach chooses which cards to pay) — unless the pay is
  // forced (no spare cards to choose between), which the engine auto-resolves.
  const requestAct = (action: LegalAction) => {
    const cost = actionCost(action);
    if (cost > 0) {
      // The payment pool: this hand, minus one instance of each card being
      // played (a card can't be both played and recycled). Only id-bearing
      // cards (your own, face-up) can be named as payment.
      const toRemove = playedCardIds(action);
      const payable: HandCardData[] = [];
      for (const card of userHand) {
        const at = card.id ? toRemove.indexOf(card.id) : -1;
        if (at >= 0) {
          toRemove.splice(at, 1); // drop just this one played instance
          continue;
        }
        if (card.id) payable.push(card);
      }
      // Only prompt when there's a genuine choice; a forced pay (exactly enough
      // cards) falls through to the engine's auto-pick.
      if (payable.length > cost) {
        setPayPrompt({ action, cost, cards: payable });
        return;
      }
    }
    onAct(action);
  };

  const disposeWith = (action: LegalAction) => {
    setSelectedActorId(null);
    requestAct(action);
  };

  // -------------------------------------------------------------------------
  // Drag a card from the hand onto the arena.
  //
  // Pointer-based (works for mouse AND touch) rather than HTML5 drag, so we
  // control the ghost. Lifecycle: pointerdown arms a candidate; the first
  // move past the threshold starts the drag (ghost mounts, the card's field
  // targets light up); pointerup hit-tests via elementFromPoint:
  //   · on a target pill  → fire that disposal
  //   · on the field      → fire the card's single unambiguous action, or
  //                         keep its targets lit so the pill is one tap away
  //   · anywhere else     → cancel
  // The ghost follows the pointer through direct style writes — no per-move
  // re-render of the whole match view.
  // -------------------------------------------------------------------------
  type DragCandidate = {
    cardId: string;
    card: HandCardData;
    startX: number;
    startY: number;
    started: boolean;
    prevSelected: string | null;
  };
  const dragRef = useRef<DragCandidate | null>(null);
  const ghostRef = useRef<HTMLDivElement>(null);
  const suppressClickRef = useRef(false);
  const [dragging, setDragging] = useState<HandCardData | null>(null);

  // Latest-value mirror so the window listeners (attached once) never go stale.
  // Drops dispatch through requestAct so a costed card opens the pay picker.
  const dragEnv = useRef({ actionsByCardId, legalActions, busy, requestAct });
  dragEnv.current = { actionsByCardId, legalActions, busy, requestAct };

  const startCardDrag = (card: HandCardData, e: React.PointerEvent) => {
    if (busy || !card.id) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const matching = actionsByCardId.get(card.id) ?? [];
    if (matching.length === 0) return; // not playable — taps still preview
    dragRef.current = {
      cardId: card.id,
      card,
      startX: e.clientX,
      startY: e.clientY,
      started: false,
      prevSelected: selectedActorId,
    };
  };

  useEffect(() => {
    const THRESHOLD = 8;
    let overPill: Element | null = null;

    const arenaEl = () => document.querySelector('[data-drop-arena]');
    const clearHover = () => {
      overPill?.classList.remove('drag-over');
      overPill = null;
      arenaEl()?.classList.remove('drag-over-arena');
    };

    const onMove = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      if (!d.started) {
        if (Math.hypot(e.clientX - d.startX, e.clientY - d.startY) < THRESHOLD) return;
        d.started = true;
        suppressClickRef.current = true;
        setDragging(d.card);
        const matching = dragEnv.current.actionsByCardId.get(d.cardId) ?? [];
        if (matching.some(isFieldTargeting)) setSelectedActorId(d.cardId);
      }
      const g = ghostRef.current;
      if (g) {
        g.style.left = `${e.clientX}px`;
        g.style.top = `${e.clientY}px`;
      }
      const under = document.elementFromPoint(e.clientX, e.clientY);
      const pill = under?.closest('[data-drop-action]') ?? null;
      if (pill !== overPill) {
        overPill?.classList.remove('drag-over');
        overPill = pill;
        overPill?.classList.add('drag-over');
      }
      arenaEl()?.classList.toggle('drag-over-arena', !!under?.closest('[data-drop-arena]'));
    };

    const resolveDrop = (d: DragCandidate, x: number, y: number) => {
      const { actionsByCardId, legalActions, busy, requestAct } = dragEnv.current;
      if (busy) {
        setSelectedActorId(d.prevSelected);
        return;
      }
      const matching = actionsByCardId.get(d.cardId) ?? [];
      const under = document.elementFromPoint(x, y);

      const pill = under?.closest('[data-drop-action]');
      if (pill) {
        const id = pill.getAttribute('data-drop-action');
        // Only accept pills that belong to the card being dragged — dropping
        // a weather card on the carrier's "Run" pill must not fire the run.
        const action =
          matching.find((a) => a.descriptorId === id) ??
          (matching.some(isFieldTargeting)
            ? legalActions.find((a) => a.descriptorId === id && isFieldTargeting(a))
            : undefined);
        if (action) {
          setSelectedActorId(null);
          requestAct(action);
          return;
        }
      }

      if (under?.closest('[data-drop-arena]')) {
        const fieldActs = matching.filter(isFieldTargeting);
        if (fieldActs.length === 1) {
          setSelectedActorId(null);
          requestAct(fieldActs[0]!);
          return;
        }
        if (fieldActs.length > 1) {
          // Ambiguous destination — leave the card selected with its targets
          // lit so the next tap lands the ball.
          setSelectedActorId(d.cardId);
          return;
        }
        if (matching.length === 1) {
          requestAct(matching[0]!);
          return;
        }
        if (matching.length > 1) {
          // Ambiguous (a player with several commit variants) — open the
          // inspect modal so the choice is explicit. (fieldActs is empty here.)
          setPreview({ card: d.card, commitActions: matching, canAim: false });
          return;
        }
      }

      // Dropped off the arena — back to where we were.
      setSelectedActorId(d.prevSelected);
    };

    const finish = (e: PointerEvent, cancelled: boolean) => {
      const d = dragRef.current;
      if (!d) return;
      dragRef.current = null;
      clearHover();
      if (!d.started) return; // never became a drag — the click handles it
      setDragging(null);
      // Clear after the synthetic click that follows pointerup has fired.
      setTimeout(() => {
        suppressClickRef.current = false;
      }, 0);
      if (cancelled) {
        setSelectedActorId(d.prevSelected);
        return;
      }
      resolveDrop(d, e.clientX, e.clientY);
    };

    const onUp = (e: PointerEvent) => finish(e, false);
    const onCancel = (e: PointerEvent) => finish(e, true);

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      clearHover();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------------------------------------------------------------------------
  // Scoring moments. Diff each team's goals/behinds against the previous
  // snapshot: a goal sweeps "GOAL!" across the field, a behind gets a quieter
  // nod. Match end raises the full-time overlay (dismissable, so the final
  // board can still be admired).
  // -------------------------------------------------------------------------
  const [scoreFlash, setScoreFlash] = useState<{
    kind: 'goal' | 'behind';
    mine: boolean;
    n: number;
  } | null>(null);
  const prevScores = useRef<Record<string, { goals: number; behinds: number }> | null>(null);

  useEffect(() => {
    const prev = prevScores.current;
    const next: Record<string, { goals: number; behinds: number }> = {};
    for (const [seatId, team] of Object.entries(view.teams)) {
      next[seatId] = { goals: team.score.goals, behinds: team.score.behinds };
    }
    prevScores.current = next;
    if (!prev) return; // first snapshot — nothing scored "just now"
    for (const [seatId, s] of Object.entries(next)) {
      const p = prev[seatId];
      if (!p) continue;
      if (s.goals > p.goals || s.behinds > p.behinds) {
        const kind: 'goal' | 'behind' = s.goals > p.goals ? 'goal' : 'behind';
        setScoreFlash((cur) => ({ kind, mine: seatId === userSeatId, n: (cur?.n ?? 0) + 1 }));
      }
    }
  }, [view.teams, userSeatId]);

  useEffect(() => {
    if (!scoreFlash) return;
    const t = setTimeout(() => setScoreFlash(null), 1750);
    return () => clearTimeout(t);
  }, [scoreFlash]);

  const matchOver = match.match.status === 'completed' || view.phase === 'ended' || !!view.winnerSeatId;
  const [resultDismissed, setResultDismissed] = useState(false);

  // A card standing on the ground was tapped. If it carries a legal action
  // right now, use it (same rules as a hand card); otherwise just preview.
  const handleOnFieldClick = (item: OnFieldCardView) => {
    const matching = actionsByCardId.get(item.cardId) ?? [];
    // The carrier (and any field-targeting piece) selects for aiming directly —
    // that's the core disposal loop. Everything else opens the inspect modal
    // (never auto-plays), consistent with how hand cards behave.
    if (matching.length > 0 && matching.some(isFieldTargeting) && !busy) {
      setSelectedActorId((cur) => (cur === item.cardId ? null : item.cardId));
      return;
    }
    setPreview({
      card: onFieldToHandCard(item),
      commitActions: matching.filter((a) => !isFieldTargeting(a)),
      canAim: false,
    });
  };

  return (
    <div
      ref={rootRef}
      style={{ height }}
      className="flex flex-col gap-2 overflow-hidden"
    >
      <TopBar
        match={match}
        view={view}
        userSeatId={userSeatId}
        seatLabels={seatLabels}
        isParticipant={isParticipant}
        legalActions={legalActions}
        busy={busy}
        onAct={onAct}
      />

      {error && (
        <div className="shrink-0 animate-deal-in rounded-md border border-ec-oxblood bg-[rgba(39,147,211,0.28)] px-3 py-1.5 text-xs text-ec-face motion-reduce:animate-none">
          {error}
        </div>
      )}

      {/* Main area: a TOP ROW (field + detail column side by side) over a
          FULL-WIDTH hand strip, so the hand spans beneath BOTH columns (owner:
          "I need my hand full width"). The relative wrapper also hosts the
          mobile panel overlay so it still covers the field AND the hand. */}
      <div className="relative flex min-h-0 flex-1 flex-col gap-2">
        {/* TOP ROW — field (arena width) beside the detail column (flex-1). */}
        <div className="flex min-h-0 flex-1 gap-2">
        {/* LEFT column — sized to the FIELD's own 17:10 footprint (no flex-1
            width), so the green field fills its box edge-to-edge: no slate
            bands beside the arena. The detail column (flex-1) eats the width
            this frees on the right. items-center keeps the aspect box from
            being stretched to the column width. */}
        <div
          className={`flex min-h-0 flex-col gap-1.5 ${
            arenaWidth
              ? // The width is derived from the field's HEIGHT (height×1.7). Cap
                // it so it can never exceed the viewport (no clipped field on a
                // narrow/tall phone) and, where the detail panel sits beside it
                // (md+), always leave the panel room. Below md the panel is a
                // drawer, so centre the arena rather than leaving a one-sided
                // band of empty background.
                'shrink-0 mx-auto md:mx-0 max-w-full md:max-w-[calc(100%-21rem)]'
              : 'min-w-0 flex-1'
          }`}
          style={arenaWidth ? { width: arenaWidth } : undefined}
        >
          {/* The field area fills the available height (flex-1) and the column's
              width; once we've measured its height, the column is set to the
              matching 17:10 width so the field fills it edge-to-edge (no side
              bands). AspectFit inside still letterboxes safely during the first
              paint before the measurement lands. */}
          <div ref={fieldAreaRef} className="relative min-h-0 w-full flex-1">
            <AflFieldView
              view={view}
              match={match}
              userSeatId={userSeatId}
              fill
              onPlayerClick={handleOnFieldClick}
              onInspectPosition={(info) => setInspectPos(info)}
              disposal={
                yourTurn && selectedActions.length > 0
                  ? { actions: selectedActions, busy, onChoose: disposeWith }
                  : undefined
              }
            />

            {/* Scoring announcement sweeping over the field. */}
            {scoreFlash && (
              <div
                key={scoreFlash.n}
                className="pointer-events-none absolute inset-0 z-40 flex items-center justify-center"
              >
                {scoreFlash.kind === 'goal' && (
                  <div className="absolute inset-0 animate-goal-flash [background:radial-gradient(ellipse_at_center,rgba(245,168,28,0.30),transparent_62%)] motion-reduce:animate-none" />
                )}
                <span
                  className={`select-none font-display font-bold uppercase tracking-tight animate-goal-flash motion-reduce:animate-none ${
                    scoreFlash.kind === 'goal'
                      ? 'text-6xl text-ec-brass-2 [text-shadow:0_3px_0_rgba(74,22,16,0.95),0_10px_30px_rgba(0,0,0,0.85)] sm:text-8xl'
                      : 'text-4xl text-ec-face [text-shadow:0_2px_0_rgba(19,22,27,0.95),0_8px_24px_rgba(0,0,0,0.85)] sm:text-5xl'
                  }`}
                >
                  {scoreFlash.kind === 'goal'
                    ? scoreFlash.mine
                      ? 'GOAL!'
                      : 'Goal — them'
                    : 'Behind'}
                </span>
              </div>
            )}

            {/* Full-time card + confetti for the winner. */}
            {matchOver && !resultDismissed && (
              <FullTimeOverlay
                view={view}
                userSeatId={userSeatId}
                seatLabels={seatLabels}
                matchId={matchId}
                onDismiss={() => setResultDismissed(true)}
              />
            )}
          </div>

        </div>

        {/* Desktop: persistent detail column — takes ALL the width the hugged
            field frees up (flex-1), at FULL row height. Panels are PINNED; only
            the event log scrolls (inside itself). A min-width keeps it usable;
            a max-width stops it sprawling on ultra-wide screens. */}
        <aside className="hidden min-w-[20rem] flex-1 pr-0.5 md:flex md:min-h-0 md:flex-col">
          <DetailPanels
            view={view}
            match={match}
            userSeatId={userSeatId}
            matchId={matchId}
            seatLabels={seatLabels}
            lastEventSummary={lastEventSummary}
            onPreviewCard={inspect}
            fillHeight
          />
        </aside>
        </div>

        {/* HAND — FULL WIDTH, beneath the field + detail row. Scrolls
            horizontally only if the cards overrun the whole viewport width (now
            far more than the old arena-capped strip). */}
        {isParticipant ? (
          <div className="flex w-full min-w-0 shrink-0 flex-col gap-1">
            {/* Height-bounded so a long wrapping hint can't grow the hand row
                and shrink the field above. The full text also lives in the
                event log. */}
            <div className="max-h-20 shrink-0 overflow-hidden">
              <DisposalPrompt
                yourTurn={yourTurn}
                hasDisposal={hasDisposal}
                phase={view.phase}
                selected={!!effectiveActorId}
                disposingAsCarrier={disposingAsCarrier}
                selectedName={effectiveName}
                pendingRun={view.pendingRun ?? null}
                canCancel={!!selectedActorId && !disposingAsCarrier}
                onCancel={() => setSelectedActorId(null)}
                clearance={view.clearance}
                ruck={view.ruck}
                userSeatId={userSeatId}
                canShoot={shotActions.length > 0}
              />
            </div>
            {(shotActions.length > 0 || flowActions.length > 0) && (
              <div className="flex flex-wrap items-stretch gap-1.5">
                {/* SHOOT — the primary action, PINNED here where nothing can
                    cover it. One gold button per variant; cost and torpedo
                    notes ride in the label. 44px-min touch targets. */}
                {shotActions.map((a) => (
                  <button
                    key={a.descriptorId}
                    type="button"
                    onClick={() => disposeWith(a)}
                    disabled={busy}
                    className="min-h-[2.75rem] animate-pop-in rounded-lg border-2 border-ec-brass-2 bg-ec-brass px-4 py-1.5 font-display text-sm font-bold uppercase tracking-wide text-ec-ink shadow-lg shadow-black/40 transition hover:-translate-y-0.5 hover:brightness-110 disabled:opacity-50 motion-reduce:animate-none"
                  >
                    🥅 {stripActorPrefix(a.label)}
                  </button>
                ))}
                {flowActions.map((a) => (
                  <button
                    key={a.descriptorId}
                    type="button"
                    onClick={() => requestAct(a)}
                    disabled={busy}
                    className="min-h-[2.75rem] rounded-md border border-ec-line bg-ec-panel px-2.5 py-1 font-display text-xs uppercase tracking-wide text-ec-face transition hover:-translate-y-0.5 hover:border-ec-brass hover:bg-ec-panel-2 disabled:opacity-50"
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            )}
            {userHand.length > 0 ? (
              <div className="flex min-w-0 max-w-full gap-1.5 overflow-x-auto pb-1 pt-1">
                {userHand.map((card, i) => {
                  const matching = card.id ? (actionsByCardId.get(card.id) ?? []) : [];
                  const playable = matching.length > 0 && !busy;
                  const beingDragged = !!dragging && dragging.id === card.id;
                  return (
                    <div
                      key={card.id ?? i}
                      className="shrink-0 animate-deal-in select-none motion-reduce:animate-none"
                      style={{ animationDelay: `${Math.min(i * 45, 400)}ms` }}
                    >
                      <div
                        onPointerDown={playable ? (e) => startCardDrag(card, e) : undefined}
                        className={`${playable ? 'cursor-grab touch-pan-x active:cursor-grabbing' : ''} ${
                          beingDragged ? 'opacity-30 saturate-50' : 'transition-opacity'
                        }`}
                      >
                        <HandCard
                          card={card}
                          size="md"
                          playable={playable}
                          selected={selectedActorId === card.id}
                          onClick={() => {
                            if (suppressClickRef.current) return;
                            handleCard(card);
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-ec-bone-dim">Your hand is empty.</p>
            )}
          </div>
        ) : (
          <p className="w-full shrink-0 text-xs text-ec-bone-dim">
            Spectating — only participants can act.
          </p>
        )}

        {/* Mobile (<md): the selected tab's panels overlay the arena above the
            tab strip. Absolute so its height never feeds back into the field
            measurement. Hidden on md+, where the side column shows everything. */}
        {mobileTab !== 'hand' && (
          <div className="absolute inset-0 z-30 overflow-y-auto rounded-lg border border-ec-line bg-ec-ground/95 p-2 md:hidden">
            <DetailPanels
              view={view}
              match={match}
              userSeatId={userSeatId}
              matchId={matchId}
              seatLabels={seatLabels}
              lastEventSummary={lastEventSummary}
              onPreviewCard={inspect}
              section={mobileTab}
            />
          </div>
        )}
      </div>

      {/* Mobile detail tabs — always visible so scoreboard / live contest /
          event log are one tap away (replaces the old hidden "Details" drawer).
          Hidden on md+, where the side column shows them inline. */}
      <nav className="flex shrink-0 gap-1 md:hidden">
        {([
          ['hand', 'Hand'],
          ['score', 'Score'],
          ['live', 'Live'],
          ['log', 'Log'],
        ] as const).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setMobileTab(key)}
            aria-pressed={mobileTab === key}
            className={`flex-1 rounded-md border px-2 py-1.5 font-display text-xs font-semibold uppercase tracking-wide transition ${
              mobileTab === key
                ? 'border-ec-brass bg-[rgba(39,147,211,0.35)] text-ec-face'
                : 'border-ec-line bg-ec-panel text-ec-bone hover:border-ec-bone'
            }`}
          >
            {label}
          </button>
        ))}
      </nav>

      {/* Drag ghost — follows the pointer via direct style writes (see onMove). */}
      {dragging && (
        <div
          ref={ghostRef}
          className="pointer-events-none fixed z-[100] -translate-x-1/2 -translate-y-[80%] rotate-6 scale-110 opacity-95 drop-shadow-[0_18px_24px_rgba(0,0,0,0.6)]"
          style={{ left: -9999, top: -9999 }}
        >
          <HandCard card={dragging} size="md" playable />
        </div>
      )}

      {preview && (
        <CardPreviewModal
          card={preview.card}
          actions={preview.commitActions}
          canAim={preview.canAim}
          busy={busy}
          onChoose={(a) => {
            setPreview(null);
            requestAct(a);
          }}
          onAim={() => {
            setSelectedActorId(preview.card.id ?? null);
            setPreview(null);
          }}
          onClose={() => setPreview(null)}
        />
      )}

      {payPrompt && (
        <RecycleCostModal
          actionLabel={payPrompt.action.label}
          cost={payPrompt.cost}
          cards={payPrompt.cards}
          busy={busy}
          onConfirm={(payIds) => {
            const action = payPrompt.action;
            setPayPrompt(null);
            onAct(action, payIds);
          }}
          onCancel={() => setPayPrompt(null)}
        />
      )}

      {inspectPos && (
        <PositionInspector
          position={inspectPos}
          onField={view.onField ?? []}
          userSeatId={userSeatId}
          seatLabels={seatLabels}
          onPreview={(card) => {
            setInspectPos(null);
            handleCard(card);
          }}
          onClose={() => setInspectPos(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Viewport-fill height — measure the box so the page itself never scrolls.
// ---------------------------------------------------------------------------

function useViewportFill(ref: React.RefObject<HTMLElement>): number | undefined {
  const [h, setH] = useState<number | undefined>(undefined);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const top = el.getBoundingClientRect().top;
      const parent = el.parentElement;
      const padB = parent ? parseFloat(getComputedStyle(parent).paddingBottom || '0') || 0 : 0;
      // innerHeight tracks the live viewport (URL bar show/hide) but, unlike
      // visualViewport.height, is stable under pinch-zoom — so zooming to read
      // a card never shrinks the arena. Matches the shell's 100dvh.
      const avail = window.innerHeight - top - padB - 2;
      setH(Math.max(420, Math.floor(avail)));
    };
    measure();
    // Re-measure after the first paint settles (web fonts, header wrap).
    const raf = requestAnimationFrame(measure);
    // On mobile the URL bar sliding in/out resizes the visual viewport; that
    // event is more reliable than window 'resize' for catching it. Without
    // this the arena keeps the stale (taller) height and its bottom — the hand
    // strip and the pay picker — gets clipped off-screen.
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    window.visualViewport?.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
      window.visualViewport?.removeEventListener('resize', measure);
    };
  }, [ref]);
  return h;
}

/**
 * Track an element's rendered HEIGHT (ResizeObserver + rAF + resize fallback).
 * The field area's height is the reliable anchor for the arena: its 17:10 width
 * is height×1.7. We size the left column to that and cap the hand to it, so the
 * arena hugs its footprint (no side bands) and the hand scrolls horizontally.
 * (CSS can't do this: aspect-ratio doesn't feed a flex item's intrinsic width.)
 */
function useElementHeight(ref: React.RefObject<HTMLElement>): number | undefined {
  const [h, setH] = useState<number | undefined>(undefined);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const height = el.getBoundingClientRect().height;
      if (height > 4) setH(Math.round(height));
    };
    measure();
    const raf = requestAnimationFrame(measure);
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [ref]);
  return h;
}

// ---------------------------------------------------------------------------
// Top bar — scores, phase, clock, resign, details toggle.
// ---------------------------------------------------------------------------

function TopBar({
  match,
  view,
  userSeatId,
  seatLabels,
  isParticipant,
  legalActions,
  busy,
  onAct,
}: {
  match: MatchPublicView;
  view: AflViewerState;
  userSeatId: string | null;
  seatLabels: Record<string, string>;
  isParticipant: boolean;
  legalActions: LegalAction[];
  busy: boolean;
  onAct: (action: LegalAction) => void;
}) {
  const seats = [...match.seats].sort((a, b) => a.orderIndex - b.orderIndex);
  const inProgress = match.match.status === 'in_progress';

  return (
    <header className="ec-surface flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 px-2.5 py-1.5 backdrop-blur">
      <Link
        to="/me/matches"
        className="text-sm text-ec-bone transition-colors hover:text-ec-face"
        title="Back to your matches"
      >
        ←
      </Link>

      {/* Score chips — the at-a-glance line. */}
      <div className="flex items-center gap-1.5">
        {seats.map((s) => {
          const team = view.teams[s.id];
          const isYou = s.id === userSeatId;
          const hasPossession = s.id === view.possessionSeatId;
          const label = seatLabels[s.id] ?? s.seatKey ?? s.id.slice(0, 6);
          return (
            <span
              key={s.id}
              className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-sm ${
                isYou ? 'border-ec-ruck/70 bg-[rgba(62,68,77,0.18)] text-ec-face' : 'border-ec-line bg-ec-ground text-ec-bone'
              }`}
              title={team ? `${team.score.goals}.${team.score.behinds}` : undefined}
            >
              <span className="max-w-[6rem] truncate font-display text-xs uppercase tracking-wide text-ec-bone-dim">{label}</span>
              {/* Keyed on the score so a change remounts it and pops. */}
              <strong
                key={team?.total ?? 0}
                className="inline-block animate-score-pop font-display tabular-nums motion-reduce:animate-none"
              >
                {team?.total ?? 0}
              </strong>
              {hasPossession && (
                <span className="animate-pulse text-ec-brass motion-reduce:animate-none" title="In possession">
                  ●
                </span>
              )}
            </span>
          );
        })}
      </div>

      <span className="font-display text-xs uppercase tracking-wide text-ec-bone-dim">
        Q{view.quarter} · <strong className="text-ec-bone">{humanizePhase(view.phase)}</strong>
      </span>

      <div className="ml-auto flex items-center gap-2">
        {!isParticipant && match.match.status !== 'waiting_for_players' && (
          <span className="rounded-full border border-ec-ruck/70 bg-[rgba(62,68,77,0.16)] px-2 py-0.5 font-display text-[10px] font-semibold uppercase tracking-wider text-ec-bone">
            Spectating
          </span>
        )}
        {inProgress && match.decisionWindow?.deadlineAt && (
          <span className="hidden items-center gap-1 font-display text-xs text-ec-bone sm:inline-flex">
            {!!userSeatId && match.decisionWindow.activeSeatId === userSeatId ? '⏱ You:' : '⏱ Opp:'}{' '}
            <MatchClock deadlineAt={match.decisionWindow.deadlineAt} />
          </span>
        )}
        {inProgress && isParticipant && (
          <ResignControl legalActions={legalActions} busy={busy} onAct={onAct} />
        )}
      </div>
    </header>
  );
}

function ResignControl({
  legalActions,
  busy,
  onAct,
}: {
  legalActions: LegalAction[];
  busy: boolean;
  onAct: (action: LegalAction) => void;
}) {
  const concede = legalActions.find((a) => a.actionKind === 'concede');
  const [confirming, setConfirming] = useState(false);

  if (!concede) {
    return (
      <span
        className="rounded-md border border-ec-line bg-ec-ground px-2 py-1 font-display text-[11px] uppercase tracking-wide text-ec-bone-dim"
        title="Resign is only available when it's your turn"
      >
        Resign n/a
      </span>
    );
  }
  if (!confirming) {
    return (
      <button
        type="button"
        onClick={() => setConfirming(true)}
        disabled={busy}
        className="rounded-md border border-ec-oxblood bg-[rgba(39,147,211,0.28)] px-2 py-1 font-display text-[11px] uppercase tracking-wide text-ec-face transition hover:border-ec-oxblood-2 disabled:opacity-50"
      >
        Resign
      </button>
    );
  }
  return (
    <span className="inline-flex items-center gap-1">
      <button
        type="button"
        onClick={() => onAct(concede)}
        disabled={busy}
        className="rounded-md border border-ec-oxblood-2 bg-ec-oxblood px-2 py-1 font-display text-[11px] font-medium uppercase tracking-wide text-ec-face transition hover:bg-ec-oxblood-2 disabled:opacity-50"
      >
        Confirm
      </button>
      <button
        type="button"
        onClick={() => setConfirming(false)}
        disabled={busy}
        className="rounded-md border border-ec-line px-2 py-1 font-display text-[11px] uppercase tracking-wide text-ec-bone transition hover:border-ec-bone disabled:opacity-50"
      >
        Cancel
      </button>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Disposal prompt — the one-line instruction above the hand.
// ---------------------------------------------------------------------------

function DisposalPrompt({
  yourTurn,
  hasDisposal,
  phase,
  selected,
  disposingAsCarrier,
  selectedName,
  pendingRun,
  canCancel,
  onCancel,
  clearance,
  ruck,
  userSeatId,
  canShoot,
}: {
  yourTurn: boolean;
  hasDisposal: boolean;
  phase: string;
  selected: boolean;
  disposingAsCarrier: boolean;
  selectedName: string;
  pendingRun: { runnerName?: string; destName?: string } | null;
  canCancel: boolean;
  onCancel: () => void;
  clearance: AflClearanceView | null;
  ruck: AflRuckView | null;
  userSeatId: string | null;
  canShoot: boolean;
}) {
  const enter = 'animate-deal-in motion-reduce:animate-none';

  // CLEARANCE — the prompt must carry the ruck verdict itself (owner mobile
  // rule: on phones the clearance panel hides behind the Live tab, so "did I
  // win the ruck / do I rove first or second?" has to be answered RIGHT HERE,
  // where the player is being asked to act).
  if (phase === 'clearance' && clearance && userSeatId) {
    const won = clearance.ruckWinnerSeatId === userSeatId;
    const verdict = (
      <strong
        className={`font-display uppercase tracking-wide ${won ? 'text-ec-go' : 'text-ec-brass-2'}`}
      >
        🏉 You {won ? 'won' : 'lost'} the ruck — you {won ? 'act LAST' : 'rove FIRST'}.
      </strong>
    );
    const oppSeatId = won ? clearance.ruckLoserSeatId : clearance.ruckWinnerSeatId;
    const theirBest = bestRover(clearance.commits[oppSeatId] ?? []);
    const theirBrief = theirBest
      ? `${theirBest.name ?? 'their rover'} (${
          theirBest.inPosition
            ? `in position T${roverTierNumber(theirBest.tier)}`
            : 'out of position'
        } · power ${theirBest.stats?.power ?? 0})`
      : null;
    if (!yourTurn) {
      return (
        <p className={`text-xs text-ec-bone-dim ${enter}`}>
          {verdict}{' '}
          {won
            ? 'They rove first — waiting on their rover…'
            : theirBrief
              ? `Their answer so far: ${theirBrief}. Waiting…`
              : 'Your rover is in — they answer last. Waiting…'}
        </p>
      );
    }
    const committedAny = Object.values(clearance.commits).some((c) => c.length > 0);
    return (
      <p
        className={`text-xs text-ec-go ${enter}`}
        title="The ruck loser roves first; the winner answers last. The single best rover wins by position → tier → power — not the most players. Equal best → fresh ruck. If the loser never roves, the winning ruckman gathers it."
      >
        {verdict}{' '}
        {theirBrief ? (
          <>
            Their best rover: <strong className="text-ec-face">{theirBrief}</strong> — tap a
            glowing player to beat it (best ONE wins, not most),{' '}
          </>
        ) : (
          <>Tap a glowing player to rove the tap (best ONE wins, not most), </>
        )}
        {!won && !committedAny ? (
          <>
            or <strong className="text-ec-face">Don't rove</strong> → their ruckman{' '}
            {clearance.ruckman.name} (power {clearance.ruckman.power}) gathers it.
          </>
        ) : (
          <>
            or <strong className="text-ec-face">Stop</strong> → the best rover so far takes it.
          </>
        )}
      </p>
    );
  }

  // RUCK — teach the order rule up front, so the coming clearance prompt
  // ("you rove FIRST/LAST") is never a surprise.
  if (phase === 'ruck_nomination') {
    if (yourTurn) {
      return (
        <p className={`text-xs text-ec-brass ${enter}`}>
          🏉 Ruck — tap a card, then <strong className="text-ec-face">Nominate</strong>{' '}
          (bluffing allowed). Player beats non-player, higher power wins — and the winner acts{' '}
          <strong className="text-ec-face">LAST</strong> in the clearance.
        </p>
      );
    }
    if (ruck?.yourNominationCardId) {
      return (
        <p className={`text-xs text-ec-bone-dim ${enter}`}>
          🏉 Ruck — your nomination is in. Waiting for theirs… (the winner acts{' '}
          <strong>LAST</strong> at the clearance)
        </p>
      );
    }
  }

  if (!yourTurn) {
    return (
      <p className={`text-xs text-ec-bone-dim ${enter}`}>
        {phase === 'run_tackle' && pendingRun
          ? `${pendingRun.runnerName ?? 'Your runner'} is running for ${pendingRun.destName ?? 'the next line'} — waiting on the tackle…`
          : 'Waiting for your opponent…'}
      </p>
    );
  }
  if (phase === 'run_tackle') {
    return (
      <p className={`text-xs text-ec-brass ${enter}`}>
        {pendingRun?.runnerName ?? 'The runner'} is running for{' '}
        {pendingRun?.destName ?? 'the next line'} — you drew a card. Tackle with a player (
        <strong className="text-ec-go">✓ stops</strong> /{' '}
        <strong className="text-ec-brass-2">ruck</strong> /{' '}
        <strong className="text-ec-bone">✗ can't</strong>) or let them run.
      </p>
    );
  }
  if (phase === 'clearance') {
    // Spectator / legacy fallback — participants get the verdict version above.
    return (
      <p className={`text-xs text-ec-go ${enter}`}>
        Clearance — tap a glowing player to <strong>rove the tap</strong>, or{' '}
        <strong className="text-ec-face">Stop</strong>. The single best rover wins it — not
        the most players.
      </p>
    );
  }
  if (selected) {
    return (
      <p className={`flex items-center gap-2 text-xs text-ec-go ${enter}`}>
        <span>
          {disposingAsCarrier ? (
            <>
              Your ball — tap a glowing target to carry / kick it with{' '}
              <strong>{selectedName}</strong>, or drag a teammate onto the field to handpass.
              {canShoot && (
                <>
                  {' '}
                  <strong className="text-ec-brass-2">
                    In range — hit the gold 🥅 SHOOT button below.
                  </strong>
                </>
              )}
            </>
          ) : (
            <>
              Handpass to <strong>{selectedName}</strong> — drop or tap the spot.
            </>
          )}
        </span>
        {canCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-ec-line px-1.5 py-0.5 text-[11px] text-ec-bone hover:border-ec-bone"
          >
            Cancel
          </button>
        )}
      </p>
    );
  }
  if (hasDisposal) {
    return (
      <p className={`text-xs text-ec-go ${enter}`}>
        Your ball — tap a glowing target to send it, or drag a teammate onto the field to handpass.
      </p>
    );
  }
  if (phase === 'kick_target') {
    return (
      <p className={`text-xs text-ec-brass ${enter}`}>
        Your kick landed — tap one of your players to <strong>lead at the mark</strong>. A
        green <strong className="text-ec-go">+N</strong> badge means that player is in
        position at the drop. Or choose <strong>Lead no one</strong> to hold your forwards
        back (the ball spills loose).
      </p>
    );
  }
  if (phase === 'kick_response') {
    return (
      <p className={`text-xs text-ec-brass ${enter}`}>
        Marking contest — tap one of your players to contest the mark. A green{' '}
        <strong className="text-ec-go">+N</strong> badge means that player is in
        position at the contest and adds the bonus.
      </p>
    );
  }
  if (phase === 'loose_ball') {
    return (
      <p className={`text-xs text-ec-brass ${enter}`}>
        Loose ball on the ground — tap one of your players to win it.
      </p>
    );
  }
  return (
    <p className={`text-xs text-ec-bone ${enter}`}>
      Your turn — tap or drag a glowing card to play it.
    </p>
  );
}

// ---------------------------------------------------------------------------
// Full-time overlay — the end-of-match moment over the field.
// ---------------------------------------------------------------------------

/** Deterministic-enough pseudo-random for confetti layout (no reseed jitter). */
function confettiPieces(count: number) {
  const colors = ['#f5a81c', '#2793d3', '#55a72f', '#3e444d', '#f4f4f2', '#ffc24a'];
  return Array.from({ length: count }, (_, i) => {
    // Hash the index so the layout is stable across re-renders.
    const r = (n: number) => {
      const x = Math.sin(i * 127.1 + n * 311.7) * 43758.5453;
      return x - Math.floor(x);
    };
    return {
      left: r(1) * 100,
      delay: r(2) * 2.6,
      duration: 2.6 + r(3) * 2,
      color: colors[i % colors.length]!,
      width: 5 + Math.round(r(4) * 5),
      height: 9 + Math.round(r(5) * 7),
      tilt: r(6) * 180,
    };
  });
}

function FullTimeOverlay({
  view,
  userSeatId,
  seatLabels,
  matchId,
  onDismiss,
}: {
  view: AflViewerState;
  userSeatId: string | null;
  seatLabels: Record<string, string>;
  matchId: string;
  onDismiss: () => void;
}) {
  const winnerSeatId = view.winnerSeatId;
  const youWon = !!winnerSeatId && winnerSeatId === userSeatId;
  const winnerLabel = winnerSeatId ? (seatLabels[winnerSeatId] ?? winnerSeatId) : null;
  const pieces = useMemo(() => (youWon ? confettiPieces(42) : []), [youWon]);
  const seatIds = Object.keys(view.teams);

  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center overflow-hidden rounded-2xl bg-ec-ground/80 backdrop-blur-sm">
      {pieces.map((p, i) => (
        <span
          key={i}
          aria-hidden
          className="absolute -top-6 animate-confetti-fall rounded-[2px] motion-reduce:hidden"
          style={{
            left: `${p.left}%`,
            width: p.width,
            height: p.height,
            backgroundColor: p.color,
            transform: `rotate(${p.tilt}deg)`,
            ['--confetti-duration' as never]: `${p.duration}s`,
            ['--confetti-delay' as never]: `${p.delay}s`,
          }}
        />
      ))}

      {/* The result card speaks the card language: bone face, oxblood frame,
          brass foil hairline. */}
      <div className="relative animate-pop-in rounded-card border-2 border-ec-oxblood bg-ec-face px-6 py-5 text-center text-ec-ink shadow-ec-card motion-reduce:animate-none">
        <div className="font-display text-[11px] font-bold uppercase tracking-[0.3em] text-ec-ink-soft">
          Full time
        </div>
        <div className="mt-1 font-display text-3xl font-bold uppercase tracking-tight">
          {youWon ? '🏆 You win!' : winnerLabel ? `${winnerLabel} wins` : 'Draw'}
        </div>
        {view.completedReason && (
          <div className="mt-0.5 text-xs text-ec-ink-soft">({view.completedReason})</div>
        )}
        <div className="mx-auto mt-3 flex items-center justify-center gap-4">
          {seatIds.map((seatId) => {
            const team = view.teams[seatId]!;
            const you = seatId === userSeatId;
            return (
              <div key={seatId} className="min-w-[5.5rem]">
                <div className={`truncate font-display text-[11px] font-semibold uppercase tracking-wider ${you ? 'text-ec-ruck' : 'text-ec-oxblood'}`}>
                  {you ? 'You' : (seatLabels[seatId] ?? 'Opponent')}
                </div>
                <div className="font-display text-4xl font-bold tabular-nums">{team.total}</div>
                <div className="text-[11px] tabular-nums text-ec-ink-soft">
                  {team.score.goals}.{team.score.behinds}
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-sm">
          <Link
            to="/me/matches"
            className="rounded-lg bg-ec-oxblood px-3 py-1.5 font-display font-semibold uppercase tracking-wide text-ec-face transition hover:-translate-y-0.5 hover:bg-ec-oxblood-2"
          >
            Back to matches
          </Link>
          <Link
            to={`/matches/${matchId}/replay`}
            className="rounded-lg border-2 border-ec-ink/25 px-3 py-1.5 font-display font-semibold uppercase tracking-wide text-ec-ink transition hover:-translate-y-0.5 hover:border-ec-ink/50"
          >
            Watch replay
          </Link>
          <button
            type="button"
            onClick={onDismiss}
            className="rounded-lg px-2 py-1.5 text-ec-ink-soft transition hover:text-ec-ink"
            title="Close and view the final board"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail panels — share the scrolling side column / mobile drawer.
// ---------------------------------------------------------------------------

/**
 * Both revealed ruck nominations as actual cards — the payoff of the bluff.
 * Persisted by the engine past the ruck teardown, so it stays inspectable
 * into open play (it used to vanish the same tick it was revealed).
 */
const LAST_CONTEST_LABEL: Record<NonNullable<AflViewerState['lastContest']>['kind'], string> = {
  ruck: 'ruck',
  mark: 'marking',
  clearance: 'clearance',
  kick_mark: 'marking',
};

/**
 * The most recent RESOLVED contest of any kind (ruck / mark / clearance /
 * kick-mark), persisted by the engine past its teardown so the coach can SEE
 * the cards that contested and read WHY it went the way it did after the play
 * has already moved on. The unified successor to the old ruck-reveal box —
 * eases the "too much happening too quickly" by holding the result still.
 */
function LastContestPanel({
  contest,
  userSeatId,
  seatLabels,
  onPreview,
}: {
  contest: NonNullable<AflViewerState['lastContest']>;
  userSeatId: string | null;
  seatLabels: Record<string, string>;
  onPreview: (card: HandCardData) => void;
}) {
  const winner = contest.winnerSeatId;
  const kindLabel = LAST_CONTEST_LABEL[contest.kind] ?? 'contest';
  // Engine summaries carry raw seat ids; render them as You / Opponent (same
  // rewrite the commentary + event log use).
  const summary = humanizeSeatRefs(contest.summary, seatLabels);
  return (
    <Panel
      tone="amber"
      title={`Last contest · ${kindLabel}${contest.kind === 'ruck' && contest.round ? ` (round ${contest.round})` : ''}`}
      right={
        <span className="truncate text-[10px] font-semibold text-white/85">
          {winner ? `${winner === userSeatId ? 'You' : (seatLabels[winner] ?? 'Opponent')} won it` : 'No winner'}
        </span>
      }
    >
      <div className="flex items-start justify-center gap-3" title={summary}>
        {contest.sides.map((side) => {
          const you = side.seatId === userSeatId;
          const won = winner === side.seatId;
          return (
            <div key={side.seatId} className="flex min-w-0 flex-col items-center gap-0.5">
              <span className={`font-display text-[10px] font-bold uppercase tracking-wider ${you ? 'text-ec-ruck' : 'text-ec-oxblood-2'}`}>
                {you ? 'You' : (seatLabels[side.seatId] ?? 'Opponent')}
                {won && ' · 🏆'}
              </span>
              {/* Bounded so a many-rover clearance can't starve the event log. */}
              <div className="flex max-h-[7.5rem] flex-wrap items-start justify-center gap-1 overflow-hidden">
                {side.cards.length === 0 ? (
                  <span className="px-2 py-6 text-[11px] text-ec-bone-dim">— no card —</span>
                ) : (
                  side.cards.map((card, i) => (
                    <div
                      key={i}
                      className={`origin-top scale-[0.7] ${won ? 'rounded-xl ring-2 ring-ec-brass' : winner ? 'opacity-65 saturate-[0.8]' : ''}`}
                    >
                      <HandCard card={card as HandCardData} size="sm" onClick={() => onPreview(card as HandCardData)} />
                    </div>
                  ))
                )}
              </div>
              <span className="text-center text-[9px] leading-tight text-ec-bone">{humanizeSeatRefs(side.detail, seatLabels)}</span>
            </div>
          );
        })}
      </div>
      {/* The WHY, in numbers (owner rule) — held still so it can be read. */}
      <p className="mt-1.5 text-[11px] leading-snug text-ec-bone">{summary}</p>
    </Panel>
  );
}

/**
 * The IN-PROGRESS marking contest (the carrier model's kick → mark) has no
 * panel of its own — it plays out on the field. This shows the committed
 * marking target as a real CARD (the responder is still choosing), using the
 * same HandCard + scale + fonts + amber tone as the Last-contest box so the two
 * read identically. Only rendered while a target is committed (option A: no box
 * outside an actual contest).
 */
function AflMarkContestPanel({
  pendingKick,
  onField,
  userSeatId,
  lastEventSummary,
  onPreview,
}: {
  pendingKick: NonNullable<AflViewerState['pendingKick']>;
  onField: AflViewerState['onField'];
  userSeatId: string | null;
  lastEventSummary: string;
  onPreview: (card: HandCardData) => void;
}) {
  const kickerIsYou = pendingKick.kickerSeatId === userSeatId;
  const found = onField?.find((c) => c.cardId === pendingKick.targetCardId)?.card ?? null;
  const targetCard = found ? (found as HandCardData) : null;
  return (
    <Panel
      tone="amber"
      title="⚔ Current contest · marking"
      right={
        <span className="truncate text-[10px] font-semibold text-white/85">
          {kickerIsYou ? 'Opponent to respond' : 'Your contest — respond'}
        </span>
      }
    >
      <div className="flex items-start justify-center gap-3">
        {/* The committed marking target — flying for the mark. */}
        <div className="flex min-w-0 flex-col items-center gap-0.5">
          <span className={`font-display text-[10px] font-bold uppercase tracking-wider ${kickerIsYou ? 'text-ec-ruck' : 'text-ec-oxblood-2'}`}>
            {kickerIsYou ? 'You' : 'Opponent'} · flying
          </span>
          <div className="flex max-h-[7.5rem] items-start justify-center">
            {targetCard ? (
              <div className="origin-top scale-[0.7]">
                <HandCard card={targetCard} size="sm" onClick={() => onPreview(targetCard)} />
              </div>
            ) : (
              <span className="px-2 py-6 text-[11px] text-ec-bone-dim">{pendingKick.targetName ?? '— marker —'}</span>
            )}
          </div>
        </div>
        {/* The responder — still choosing whether / who to contest. */}
        <div className="flex min-w-0 flex-col items-center gap-0.5">
          <span className={`font-display text-[10px] font-bold uppercase tracking-wider ${kickerIsYou ? 'text-ec-oxblood-2' : 'text-ec-ruck'}`}>
            {kickerIsYou ? 'Opponent' : 'You'} · responding
          </span>
          <span className="px-2 py-6 text-[11px] text-ec-bone-dim">choosing…</span>
        </div>
      </div>
      <p className="mt-1.5 text-[11px] leading-snug text-ec-bone">{lastEventSummary}</p>
    </Panel>
  );
}

function DetailPanels({
  view,
  match,
  userSeatId,
  matchId,
  seatLabels,
  lastEventSummary,
  onPreviewCard,
  fillHeight = false,
  section = 'all',
}: {
  view: AflViewerState;
  match: MatchPublicView;
  userSeatId: string | null;
  matchId: string;
  seatLabels: Record<string, string>;
  lastEventSummary: string;
  onPreviewCard: (card: HandCardData) => void;
  /** Pin every panel and give the remaining height to the event log
   *  (which scrolls internally). Used by the desktop sidebar. */
  fillHeight?: boolean;
  /**
   * Which slice to render. 'all' = the full desktop column. The mobile tab
   * strip renders one slice at a time: 'score' (scoreboard + conditions),
   * 'live' (commentary + the active contest/ruck/clearance) or 'log'.
   */
  section?: 'all' | 'score' | 'live' | 'log';
}) {
  const winnerSeatId = view.winnerSeatId;
  const winnerLabel = winnerSeatId ? (seatLabels[winnerSeatId] ?? winnerSeatId) : null;
  const showScore = section === 'all' || section === 'score';
  const showLive = section === 'all' || section === 'live';
  const showLog = section === 'all' || section === 'log';

  // Scoreboard (+ full-time banner). Owner moved this to the TOP-RIGHT of the
  // detail column so the contest info gets the left side to itself.
  const scorePanels = (
    <>
      {showScore && winnerSeatId && (
        <Panel tone="emerald" title="Full time">
          <p className="text-sm text-ec-bone">
            Winner: <strong className="text-ec-face">{winnerLabel}</strong>
            {view.completedReason ? ` (${view.completedReason})` : ''}.
          </p>
        </Panel>
      )}
      {showScore && <AflScoreBoard view={view} match={match} userSeatId={userSeatId} />}
    </>
  );

  // The live / last contest — at most one is ever active at a time. Gets the
  // LEFT column to itself (full height) now the scoreboard has moved out.
  const contestPanels = (
    <>
      {showLive && view.ruck && <AflRuckPanel ruck={view.ruck} match={match} userSeatId={userSeatId} />}
      {showLive && view.clearance && (
        <AflClearancePanel
          clearance={view.clearance}
          match={match}
          userSeatId={userSeatId}
          onPreview={onPreviewCard}
        />
      )}
      {showLive && view.contest && <AflContestPanel contest={view.contest} userSeatId={userSeatId} />}
      {/* CURRENT contest · marking — the carrier marking contest has no panel of
          its own; show the committed marking target as a CARD (matching the
          Last-contest box). Only while a target is committed — option A: NO box
          when it isn't an actual contest (your ball / loose ball). */}
      {showLive && view.pendingKick?.targetCardId && !view.ruck && !view.clearance && !view.contest && (
        <AflMarkContestPanel
          pendingKick={view.pendingKick}
          onField={view.onField}
          userSeatId={userSeatId}
          lastEventSummary={lastEventSummary}
          onPreview={onPreviewCard}
        />
      )}
      {/* LAST contest — the persistent result of whatever just resolved (ruck,
          mark, clearance or kick-mark), held still so it can be reviewed. */}
      {showLive && view.lastContest && (
        <LastContestPanel
          contest={view.lastContest}
          userSeatId={userSeatId}
          seatLabels={seatLabels}
          onPreview={onPreviewCard}
        />
      )}
    </>
  );

  // A single tab slice (mobile): just the chosen panels, scrolling inside the
  // overlay that hosts them. 'live' with nothing happening shows a hint.
  if (section !== 'all') {
    return (
      <div className="space-y-2">
        {scorePanels}
        {contestPanels}
        {showLog && <EventLog matchId={matchId} seatLabels={seatLabels} />}
        {showLive && !view.ruck && !view.clearance && !view.contest && !view.pendingKick?.targetCardId && (
          <p className="px-1 text-xs text-ec-bone-dim">No live contest right now — check Score or Log.</p>
        )}
      </div>
    );
  }

  if (fillHeight) {
    // Owner layout (desktop detail column): the live/last CONTEST takes the LEFT
    // (full height now the scoreboard has moved out); the SCOREBOARD is pinned
    // TOP-RIGHT with the detailed event log filling beneath it and scrolling
    // INSIDE itself — so the log shrinks as the scoreboard takes the top, which
    // is fine because it's the scroll region (a page scroll is never allowed).
    // On a narrow detail column the two halves STACK so nothing gets squeezed.
    return (
      <div className="flex h-full min-h-0 flex-col gap-2 [@media(min-width:1400px)]:flex-row">
        {/* LEFT — contest info, full column height. */}
        <div className="flex min-h-0 shrink-0 flex-col gap-2 overflow-y-auto [@media(min-width:1400px)]:w-[50%] [@media(min-width:1400px)]:max-w-[54%]">
          {contestPanels}
        </div>
        {/* RIGHT — scoreboard pinned top-right, event log fills + scrolls below. */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2">
          <div className="shrink-0">{scorePanels}</div>
          <div className="min-h-0 flex-1">
            <EventLog matchId={matchId} seatLabels={seatLabels} layout="fill" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {scorePanels}
      {contestPanels}
      <EventLog matchId={matchId} seatLabels={seatLabels} />
    </div>
  );
}
